import React from 'react';
import { Dialog, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';

import { Button } from '../../../components/core';
import { DialogCloseButton } from '../../../components/core/DialogCloseButton';

type Props = {
  open: boolean;
  onClose: () => void;
  /** Currently saved enabled state from agentic settings. */
  enabled: boolean;
  /** Initial draft value when the modal opens (usually the toggled intent). */
  initialEnabled: boolean;
  isSaving?: boolean;
  onConfirm: (enabled: boolean) => Promise<void>;
};

export const AgentSettingsModal = ({
  open,
  onClose,
  enabled,
  initialEnabled,
  isSaving = false,
  onConfirm,
}: Props): JSX.Element => {
  const handleDone = async () => {
    if (initialEnabled === enabled) {
      onClose();
      return;
    }
    await onConfirm(initialEnabled);
  };

  return (
    <Dialog
      open={open}
      onClose={isSaving ? undefined : onClose}
      PaperProps={{
        sx: {
          borderRadius: 4,
          minWidth: { xs: '90%', md: '560px' },
          p: 3,
          position: 'relative',
        },
      }}
    >
      <DialogCloseButton onClose={onClose} disabled={isSaving} />
      <DialogTitle sx={{ fontWeight: 600, p: 0, mb: 1, pr: 5 }}>
        {initialEnabled ? 'Ready to go live?' : 'Take your agent offline?'}
      </DialogTitle>
      <Typography sx={{ mb: 3, color: 'text.secondary', fontSize: 14, maxWidth: 560 }}>
        {initialEnabled
          ? 'Shoppers will be able to chat with your agent on your storefront.'
          : 'Your agent will be hidden from shoppers. You can turn it back on anytime.'}
      </Typography>

      <DialogContent sx={{ p: 0 }}>
        <Stack
          direction="row"
          justifyContent="flex-end"
          spacing={1}
          sx={{ '& button': { height: 48 } }}
        >
          <Button
            theme="secondary"
            text="Cancel"
            onClick={onClose}
            disabled={isSaving}
            dataTestId="ella-agent-settings-cancel"
            sx={{ minWidth: 120 }}
          />
          <Button
            theme="primary"
            text={isSaving ? 'Saving...' : initialEnabled ? 'Go live' : 'Take offline'}
            onClick={() => {
              void handleDone();
            }}
            disabled={isSaving}
            loading={isSaving}
            dataTestId="ella-agent-settings-done"
            sx={{ minWidth: 120 }}
          />
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
